import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import { 
  SortableContext, 
  sortableKeyboardCoordinates, 
  verticalListSortingStrategy,
  arrayMove 
} from '@dnd-kit/sortable';
import { 
  getQuiz, 
  updateQuiz, 
  deleteQuiz, 
  addQuestion, 
  updateQuestion, 
  deleteQuestion, 
  reorderQuestions 
} from '../utils/api';
import { sanitizeText, FIELD_LIMITS, isValidQuizId } from '../utils/sanitize';
import QuestionForm from './QuestionForm';
import QuestionCard from './QuestionCard';

function QuizManager() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  
  // Quiz state
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Edit quiz info state
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Start drag after 8px movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch quiz data
  const fetchQuiz = useCallback(async () => {
    if (!isValidQuizId(quizId)) {
      setError('Invalid quiz ID');
      setLoading(false);
      return;
    }

    try {
      const data = await getQuiz(quizId);
      setQuiz(data);
      setQuestions(data.questions || []);
      setEditTitle(data.title);
      setEditDescription(data.description || '');
    } catch (err) {
      console.error('Error fetching quiz:', err);
      if (err.status === 404) {
        setError('Quiz not found');
      } else {
        setError(err.message || 'Failed to load quiz');
      }
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  // Handle quiz info update
  const handleSaveInfo = async () => {
    const title = sanitizeText(editTitle, FIELD_LIMITS.title);
    if (!title) {
      alert('Quiz title is required');
      return;
    }

    setSavingInfo(true);
    try {
      await updateQuiz(quizId, {
        title: title,
        description: sanitizeText(editDescription, FIELD_LIMITS.description)
      });
      setQuiz(prev => ({
        ...prev,
        title: title,
        description: editDescription
      }));
      setIsEditingInfo(false);
    } catch (err) {
      alert(err.message || 'Failed to update quiz');
    } finally {
      setSavingInfo(false);
    }
  };

  // Handle quiz deletion
  const handleDeleteQuiz = async () => {
    if (window.confirm('Are you sure you want to delete this entire quiz? This cannot be undone.')) {
      try {
        await deleteQuiz(quizId);
        navigate('/');
      } catch (err) {
        alert(err.message || 'Failed to delete quiz');
      }
    }
  };

  // Handle adding a new question
  const handleAddQuestion = async (questionData) => {
    await addQuestion(quizId, questionData);
    fetchQuiz(); // Refresh to get the new question with its ID
  };

  // Handle updating a question
  const handleUpdateQuestion = async (questionId, questionData) => {
    await updateQuestion(questionId, questionData);
    setQuestions(prev => 
      prev.map(q => q.id === questionId ? { ...q, ...questionData } : q)
    );
  };

  // Handle deleting a question
  const handleDeleteQuestion = async (questionId) => {
    await deleteQuestion(questionId);
    setQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  // Handle drag end for reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = questions.findIndex(q => q.id === active.id);
    const newIndex = questions.findIndex(q => q.id === over.id);

    // Optimistically update UI
    const newQuestions = arrayMove(questions, oldIndex, newIndex);
    setQuestions(newQuestions);

    // Send reorder request to backend
    try {
      await reorderQuestions(quizId, newQuestions.map(q => q.id));
    } catch (err) {
      console.error('Failed to reorder:', err);
      // Revert on error
      fetchQuiz();
    }
  };

  // Copy play link to clipboard
  const handleCopyLink = async () => {
    const playUrl = `${window.location.origin}/play/${quizId}`;
    try {
      await navigator.clipboard.writeText(playUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = playUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="quiz-manager-container">
        <div className="loading">Loading quiz...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="quiz-manager-container">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const playUrl = `${window.location.origin}/play/${quizId}`;

  return (
    <div className="quiz-manager-container">
      <div className="header">
        <button className="btn btn-back" onClick={() => navigate('/')}>
          &larr; Back
        </button>
        <h2>Manage Quiz</h2>
      </div>

      {/* Quiz Info Section */}
      <div className="quiz-info-card">
        {isEditingInfo ? (
          <div className="quiz-info-edit">
            <div className="form-group">
              <label>Quiz Title</label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={FIELD_LIMITS.title}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                maxLength={FIELD_LIMITS.description}
                rows={2}
              />
            </div>
            <div className="form-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setEditTitle(quiz.title);
                  setEditDescription(quiz.description || '');
                  setIsEditingInfo(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveInfo}
                disabled={savingInfo}
              >
                {savingInfo ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="quiz-info-display">
            <div className="quiz-info-text">
              <h3>{quiz.title}</h3>
              {quiz.description && <p>{quiz.description}</p>}
              <span className="quiz-meta">
                {questions.length} question{questions.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="quiz-info-actions">
              <button 
                className="btn btn-secondary btn-small" 
                onClick={() => setIsEditingInfo(true)}
              >
                Edit Info
              </button>
              <button 
                className="btn btn-delete btn-small" 
                onClick={handleDeleteQuiz}
              >
                Delete Quiz
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Share Section */}
      <div className="share-section">
        <div className="share-info">
          <span className="share-label">Share this quiz:</span>
          <code className="share-url">{playUrl}</code>
        </div>
        <div className="share-actions">
          <button 
            className="btn btn-secondary btn-small" 
            onClick={handleCopyLink}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button 
            className="btn btn-primary btn-small"
            onClick={() => navigate(`/play/${quizId}`)}
            disabled={questions.length === 0}
          >
            Play Quiz
          </button>
        </div>
      </div>

      {/* Add Question Form */}
      <div className="add-question-section">
        <h3>Add New Question</h3>
        <QuestionForm onSubmit={handleAddQuestion} />
      </div>

      {/* Questions List with Drag and Drop */}
      <div className="questions-section">
        <h3>Questions ({questions.length})</h3>
        {questions.length === 0 ? (
          <div className="empty-questions">
            <p>No questions yet. Add your first question above!</p>
          </div>
        ) : (
          <div className="drag-hint">
            Drag questions to reorder them
          </div>
        )}
        
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={questions.map(q => q.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="questions-list">
              {questions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  onEdit={handleUpdateQuestion}
                  onDelete={handleDeleteQuestion}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

export default QuizManager;
